/* eslint-disable react/prop-types */
import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import './PqtFormSelect.css';

const MENU_Z = 10120;
/** Room for sticky footer row (padding + input + buttons) so it is not clipped by dropdown maxHeight */
const MENU_FOOTER_RESERVE_PX = 76;

function normalizeOptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (o != null && typeof o === 'object' && 'value' in o) {
        const label = o.label != null ? String(o.label) : String(o.value);
        return { ...o, label };
      }
      return { value: o, label: String(o) };
    })
    .filter((o) => o.value !== undefined && o.value !== null && o.value !== '');
}

/**
 * CRC-style single select for PQT test modal: light panel, teal focus, menu portaled above modal mask.
 */
export default function PqtFormSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  disabled = false,
  className = '',
  variant = 'default',
  /** Optional trailing control per option (e.g. delete); use onMouseDown + stopPropagation on actions */
  renderOptionExtra,
  /** Sticky footer inside the dropdown (e.g. “add new” row); clicks do not select an option */
  menuFooter,
}) {
  const opts = normalizeOptions(options);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [listMaxHeight, setListMaxHeight] = useState(undefined);
  const hasMenuFooter = Boolean(menuFooter);

  const updatePosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const fullMax = Math.min(320, Math.max(120, window.innerHeight - r.bottom - 16));
    const listCap = hasMenuFooter
      ? Math.max(72, fullMax - MENU_FOOTER_RESERVE_PX)
      : fullMax;
    setListMaxHeight(listCap);
    setMenuStyle({
      position: 'fixed',
      top: Math.round(r.bottom + 4),
      left: Math.round(r.left),
      width: Math.round(r.width),
      maxHeight: fullMax,
      zIndex: MENU_Z,
    });
  }, [hasMenuFooter]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePosition]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (t.closest && t.closest('.pqt-form-select__dropdown')) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = opts.find((o) => o.value === value);
  const display =
    selected != null
      ? selected.label
      : value !== undefined && value !== null && value !== ''
        ? String(value)
        : '';

  const vCls = variant === 'compact' ? ' pqt-form-select--compact' : '';
  const rowCls = typeof renderOptionExtra === 'function' ? ' pqt-form-select__option--row' : '';

  const showMenu = open && !disabled && (opts.length > 0 || menuFooter);

  const menu = showMenu ? (
    <div className="pqt-form-select__dropdown" style={menuStyle}>
      {opts.length > 0 ? (
        <ul
          className="pqt-form-select__menu"
          role="listbox"
          style={listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
        >
          {opts.map((opt) => (
            <li
              key={String(opt.value)}
              role="option"
              aria-selected={opt.value === value}
              className={`pqt-form-select__option${rowCls}${
                opt.value === value ? ' pqt-form-select__option--active' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="pqt-form-select__option-text">{opt.label}</span>
              {typeof renderOptionExtra === 'function' ? (
                <span
                  className="pqt-form-select__option-extra"
                  onMouseDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  {renderOptionExtra(opt)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {menuFooter ? (
        <div
          className="pqt-form-select__menu-footer"
          onMouseDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          {menuFooter}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className={`pqt-form-select${vCls} ${className}`.trim()} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`pqt-form-select__trigger${open ? ' pqt-form-select__trigger--open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span
          className={`pqt-form-select__value${display === '' ? ' pqt-form-select__value--placeholder' : ''}`}
        >
          {display || placeholder}
        </span>
        <ChevronDown className="pqt-form-select__chev" size={18} strokeWidth={2} aria-hidden />
      </button>
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}
